"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import type { CapabilityKey, EffectivePermissions, SidebarNavConfig } from '@/lib/permissionTypes';
import { isSidebarRouteAlwaysEnabled, pathnameMatchesNavRoute } from '@/lib/permissionTypes';
import { isStaffRole } from '@/lib/roles';
import { loadStaffShell } from '@/lib/perf/loadStaffShell';
import { peekStaffShellCache, readStaffShellCache, writeStaffShellCache } from '@/lib/perf/staffShellCache';
import { markStudentShellSkipped, noteShellServer, reopenShellSample } from '@/lib/perf/shellChain';

interface PermissionsContextValue {
  loading: boolean;
  /** True once nav order has arrived from cache or the server (an empty order is valid). */
  navResolved: boolean;
  sidebarOrder: string[];
  isSuperuser: boolean;
  title: string | null;
  /** Undefined permissions (still loading, or not staff) never grant access — callers should gate on `loading` first. */
  hasCapability: (cap: CapabilityKey) => boolean;
  /** Routes turned off in Sidebar Order (global or title). Empty while loading / non-staff. */
  disabledNavRoutes: string[];
  /** False when this page was toggled off in Sidebar Order (Dashboard always true). */
  isNavRouteEnabled: (pathname: string) => boolean;
}

const EMPTY_ORDER: string[] = [];
const EMPTY_DISABLED: string[] = [];

const PermissionsContext = createContext<PermissionsContextValue>({
  loading: true,
  navResolved: false,
  sidebarOrder: EMPTY_ORDER,
  isSuperuser: false,
  title: null,
  hasCapability: () => false,
  disabledNavRoutes: EMPTY_DISABLED,
  isNavRouteEnabled: () => true,
});

export function PermissionsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, loading: authLoading } = useAuth();
  const [perms, setPerms] = useState<EffectivePermissions | null>(null);
  const [nav, setNav] = useState<SidebarNavConfig>({ order: EMPTY_ORDER, disabled: EMPTY_DISABLED });
  const [loading, setLoading] = useState(true);
  const [navResolved, setNavResolved] = useState(false);
  const serverApplied = useRef(false);
  const studentMarked = useRef(false);
  const onStaffRoute = pathname.startsWith('/staff');

  // Staff shell does not wait on the client profile read. Cookies are already
  // on the document request (proxy.ts verified them), so the action can run
  // in parallel with AuthContext's profile fetch.
  useEffect(() => {
    if (!onStaffRoute) return;
    reopenShellSample();
    let cancelled = false;

    async function run(retry: boolean) {
      const shell = await loadStaffShell({ retry });
      if (cancelled) return;
      serverApplied.current = true;
      setPerms(shell.permissions);
      setNav(shell.nav);
      setNavResolved(true);
      writeStaffShellCache({
        userId: shell.userId,
        permissions: shell.permissions,
        nav: shell.nav,
        savedAt: Date.now(),
      });
      noteShellServer({
        serverMs: shell.timing.serverMs,
        coldStart: shell.timing.coldStart,
        isolateAgeMs: shell.timing.isolateAgeMs,
        callIndex: shell.timing.callIndex,
        cacheHit: false,
        role: shell.permissions.role,
        isSuperuser: shell.permissions.isSuperuser,
        title: shell.permissions.title,
      });
    }

    run(false)
      .catch(async err => {
        console.error('[PermissionsContext] getMyStaffShell failed:', err);
        try {
          await run(true);
        } catch (retryErr) {
          console.error('[PermissionsContext] retry failed:', retryErr);
          if (!cancelled && !serverApplied.current) {
            setNavResolved(true);
          }
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [onStaffRoute]);

  // Last-good nav for this user, same tab only. Never skips the network refresh above.
  useEffect(() => {
    if (!onStaffRoute || serverApplied.current) return;
    const cached = user?.id ? readStaffShellCache(user.id) : peekStaffShellCache();
    if (!cached) return;
    setPerms(cached.permissions);
    setNav(cached.nav);
    setNavResolved(true);
    setLoading(false);
    noteShellServer({
      serverMs: 0,
      coldStart: false,
      isolateAgeMs: 0,
      callIndex: 0,
      cacheHit: true,
      role: cached.permissions.role,
      isSuperuser: cached.permissions.isSuperuser,
      title: cached.permissions.title,
    });
  }, [onStaffRoute, user?.id]);

  useEffect(() => {
    if (onStaffRoute) return;
    if (authLoading) return;
    serverApplied.current = false;
    setPerms(null);
    setNav({ order: EMPTY_ORDER, disabled: EMPTY_DISABLED });
    setNavResolved(true);
    setLoading(false);
    if (user && !isStaffRole(user.role) && !studentMarked.current) {
      studentMarked.current = true;
      markStudentShellSkipped(user.role);
    }
  }, [onStaffRoute, authLoading, user?.id, user?.role]);

  useEffect(() => {
    if (authLoading || user?.id || onStaffRoute) return;
    serverApplied.current = false;
    studentMarked.current = false;
    setPerms(null);
    setNav({ order: EMPTY_ORDER, disabled: EMPTY_DISABLED });
    setNavResolved(true);
    setLoading(false);
  }, [authLoading, user?.id, onStaffRoute]);

  const hasCapability = useCallback(
    (cap: CapabilityKey) => perms?.permissions[cap] === true,
    [perms],
  );
  const disabledNavRoutes = nav.disabled;
  const isNavRouteEnabled = useCallback((path: string) => {
    if (isSidebarRouteAlwaysEnabled(path)) return true;
    return !disabledNavRoutes.some(to => pathnameMatchesNavRoute(path, to));
  }, [disabledNavRoutes]);

  const value = useMemo<PermissionsContextValue>(() => ({
    loading: onStaffRoute ? loading : (authLoading || loading),
    navResolved,
    sidebarOrder: nav.order,
    isSuperuser: perms?.isSuperuser ?? false,
    title: perms?.title ?? null,
    hasCapability,
    disabledNavRoutes,
    isNavRouteEnabled,
  }), [
    onStaffRoute,
    loading,
    authLoading,
    navResolved,
    nav.order,
    perms,
    hasCapability,
    disabledNavRoutes,
    isNavRouteEnabled,
  ]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
